import { ChevronRight, Home } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDocumentsStore } from '../store';
import { useDomains } from '@/hooks/useDomains';
import { useDomainFolders } from '@/hooks/useDomains';
import { useEmployees } from '@/features/employees/hooks';
import { useCallback } from 'react';

export const NavigationBreadcrumb = () => {
  const { t } = useTranslation();
  const {
    selectedDomainUuid,
    selectedFolderUuid,
    selectedEmployeeUuid,
    setSelectedDomainUuid,
    setSelectedFolderUuid,
    setSelectedEmployeeUuid,
  } = useDocumentsStore();

  const { domains } = useDomains();
  const { folders } = useDomainFolders(selectedDomainUuid || '');
  const { data: employeesData } = useEmployees(
    selectedFolderUuid || undefined,
    { limit: 1000 } // Get all employees for the folder
  );

  const handleHomeClick = useCallback(() => {
    setSelectedDomainUuid(null);
    setSelectedFolderUuid(null);
    setSelectedEmployeeUuid(null);
  }, [setSelectedDomainUuid, setSelectedFolderUuid, setSelectedEmployeeUuid]);

  const handleDomainClick = useCallback(() => {
    setSelectedFolderUuid(null);
    setSelectedEmployeeUuid(null);
  }, [setSelectedFolderUuid, setSelectedEmployeeUuid]);

  const handleFolderClick = useCallback(() => {
    setSelectedEmployeeUuid(null);
  }, [setSelectedEmployeeUuid]);

  // Only show breadcrumb if at least one filter is active
  if (!selectedDomainUuid && !selectedFolderUuid && !selectedEmployeeUuid) {
    return null;
  }

  const selectedDomain = domains.find(d => d.uuid === selectedDomainUuid);
  const selectedFolder = folders.find(f => f.uuid === selectedFolderUuid);
  const selectedEmployee = employeesData?.data.find(
    e => e.uuid === selectedEmployeeUuid
  );

  return (
    <nav
      className="mb-6 flex items-center gap-2 text-sm"
      aria-label="Breadcrumb"
    >
      <button
        onClick={handleHomeClick}
        className="hover:text-primary-600 flex items-center gap-1 text-neutral-600 transition-colors"
      >
        <Home className="h-4 w-4" />
        <span>{t('documents.navigation.all', 'Tous les documents')}</span>
      </button>

      {selectedDomainUuid && (
        <>
          <ChevronRight className="h-4 w-4 text-neutral-400" />
          <button
            onClick={handleDomainClick}
            className="hover:text-primary-600 text-neutral-700 transition-colors"
          >
            {selectedDomain?.name || selectedDomainUuid}
          </button>
        </>
      )}

      {selectedFolderUuid && (
        <>
          <ChevronRight className="h-4 w-4 text-neutral-400" />
          <button
            onClick={handleFolderClick}
            className="hover:text-primary-600 text-neutral-700 transition-colors"
          >
            {selectedFolder?.name || selectedFolderUuid}
          </button>
        </>
      )}

      {selectedEmployeeUuid && (
        <>
          <ChevronRight className="h-4 w-4 text-neutral-400" />
          <span className="font-medium text-neutral-900">
            {selectedEmployee
              ? `${selectedEmployee.nom} ${selectedEmployee.prenom} - ${selectedEmployee.matriculeSalarie}`
              : selectedEmployeeUuid}
          </span>
        </>
      )}
    </nav>
  );
};
